import * as React from 'react';
import { THeader } from './Header.types';
import './Header.styled.scss';
import { Badge, SelectChangeEvent } from '@mui/material';
import { Button, IconButton, Cart, Select } from 'components';
import { ShoppingCart } from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router';
import { APP_ROUTES } from '../../../constants/routes';
import { CategoriesMenu } from './components/CategoriesMenu/CategoriesMenu';
import { MobileCategoriesDrawer } from './components/MobileCategoriesDrawer/MobileCategoriesDrawer';
import { useLocalCart } from '../../../hooks/useLocalCart';
import classNames from 'classnames';
import { useModals } from 'hooks/useModals';
import { MODALS } from 'store/contexts/ModalContext/modals';
import { useDeviceType } from 'hooks/useDeviceType';
import { useAppDispatch, useAppSelector } from 'store/store';
import { EUserRoles } from 'api/users/users.types';
import { logoutUserAsyncThunk } from 'store/users/users.action';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';
import { localeSelectOptions } from 'store/localization/localization.constants';
import { changeLocaleAction } from 'store/localization/localization.slices';
import { ESupportedLocales } from 'store/localization/localization.enums';

export const Header: THeader = () => {
  const navigate = useNavigate();
  const localize = useLocalize();
  const { data: currentUser } = useAppSelector(
    (state) => state.users.currentUser
  );

  const localeFromStore = useAppSelector(
    (state) => state.localization.localization.data?.locale
  );

  /** Must match `localeSelectOptions` / `MenuItem` values (`option.id`). */
  const locale: ESupportedLocales = React.useMemo(() => {
    const v = localeFromStore ?? ESupportedLocales.EN;
    return Object.values(ESupportedLocales).includes(v as ESupportedLocales)
      ? (v as ESupportedLocales)
      : ESupportedLocales.EN;
  }, [localeFromStore]);

  const { isMobile, isTablet, isDesktop } = useDeviceType();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { openModal } = useModals();
  const [cartOpen, setCartOpen] = React.useState(false);
  const { itemCount: cartItemCount } = useLocalCart();

  const isPatwayActive = (path: string) => pathname === path;

  const handleCartOpen = () => {
    setCartOpen(true);
  };

  const handleCartClose = () => {
    setCartOpen(false);
  };

  const handleRegistrationOpen = () => {
    openModal(MODALS.REGISTRATION);
  };

  const handleLoginOpen = () => {
    openModal(MODALS.LOGIN);
  };

  const handleLogout = () => {
    dispatch(
      logoutUserAsyncThunk({ onSuccess: () => navigate(APP_ROUTES.HOME) })
    );
  };

  const handleHeaderLocaleChange = (event: SelectChangeEvent<string>) => {
    dispatch(changeLocaleAction(event.target.value as ESupportedLocales));
  };

  return (
    <>
      <header className="header" id="app_header">
        <div className="header__container">
          <div className="header__main-content">
            {isDesktop && (
              <>
                <Link
                  to={APP_ROUTES.HOME}
                  className={classNames(
                    'caption',
                    'header__main-content__links__link',
                    {
                      'header__main-content__links__link--active':
                        isPatwayActive(APP_ROUTES.PRODUCTS),
                    }
                  )}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/logo.png`}
                    alt={localize('header.brand.title') as string}
                    className="header__main-content__logo-image"
                  />
                </Link>
                <div className="header__main-content__links">
                  <CategoriesMenu />
                  <Link
                    to={APP_ROUTES.ABOUT_US}
                    className={classNames(
                      'caption',
                      'header__main-content__links__link',
                      {
                        'header__main-content__links__link--active':
                          isPatwayActive(APP_ROUTES.ABOUT_US),
                      }
                    )}
                  >
                    {localize('header.link.about.us')}
                  </Link>
                  <Link
                    to={APP_ROUTES.CONTACTS}
                    className={classNames(
                      'caption',
                      'header__main-content__links__link',
                      {
                        'header__main-content__links__link--active':
                          isPatwayActive(APP_ROUTES.CONTACTS),
                      }
                    )}
                  >
                    {localize('header.link.contact')}
                  </Link>
                  {currentUser?.role === EUserRoles.ADMIN && (
                    <Link
                      to={APP_ROUTES.ADMIN_PANEL}
                      className={classNames(
                        'caption',
                        'header__main-content__links__link',
                        {
                          'header__main-content__links__link--active':
                            isPatwayActive(APP_ROUTES.ADMIN_PANEL),
                        }
                      )}
                    >
                      {localize('header.link.admin.panel')}
                    </Link>
                  )}
                </div>
              </>
            )}

            {(isTablet || isMobile) && (
              <div className="header__main-content__links">
                <MobileCategoriesDrawer />
              </div>
            )}
          </div>
          <div className="header__right-content">
            <div className="header__locale-select">
              <Select
                id="header-locale-select"
                label={localize('general.label.locale') as string}
                labelId="header-locale-select"
                size="small"
                value={locale}
                onChange={handleHeaderLocaleChange}
                minWidth="88px"
                maxWidth="120px"
                className="header__locale-select-field"
                options={localeSelectOptions}
              />
            </div>
            {pathname !== APP_ROUTES.CHECKOUT && (
              <IconButton
                onClick={handleCartOpen}
                className="header__cart-button"
                aria-label={localize('header.aria.shopping.cart') as string}
              >
                <Badge badgeContent={cartItemCount} color="error">
                  <ShoppingCart fontSize={isDesktop ? 'medium' : 'small'} />
                </Badge>
              </IconButton>
            )}
            {isDesktop &&
              (currentUser ? (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleLogout}
                >
                  {localize('general.button.logout')}
                </Button>
              ) : (
                <>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleLoginOpen}
                  >
                    {localize('general.button.signin')}
                  </Button>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={handleRegistrationOpen}
                  >
                    {localize('general.button.signup')}
                  </Button>
                </>
              ))}
          </div>
        </div>
      </header>
      <Cart open={cartOpen} onClose={handleCartClose} />
    </>
  );
};
